import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';

const ICON_MAP: Record<string, string> = {
  course_approval: 'ri-graduation-cap-line',
  enrollment: 'ri-user-add-line',
  achievement: 'ri-award-line',
  payment: 'ri-coins-line',
  webinar: 'ri-live-line',
  new_content: 'ri-play-circle-line',
  reminder: 'ri-alarm-line',
  promo: 'ri-gift-line',
  default: 'ri-notification-3-line',
};

const BG_MAP: Record<string, string> = {
  course_approval: 'rgba(234,179,8,0.15)',
  enrollment: 'rgba(34,197,94,0.12)',
  achievement: 'rgba(212,175,55,0.18)',
  payment: 'rgba(34,197,94,0.12)',
  webinar: 'rgba(212,175,55,0.15)',
  new_content: 'rgba(99,102,241,0.12)',
  reminder: 'rgba(249,115,22,0.12)',
  promo: 'rgba(212,175,55,0.18)',
  default: 'rgba(255,255,255,0.06)',
};

const TEXT_MAP: Record<string, string> = {
  course_approval: '#facc15',
  enrollment: '#4ade80',
  achievement: '#D4AF37',
  payment: '#4ade80',
  webinar: '#D4AF37',
  new_content: '#818cf8',
  reminder: '#fb923c',
  promo: '#D4AF37',
  default: '#9ca3af',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function NavNotificationBell() {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [prevUnread, setPrevUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Pulse animation on new notification
  useEffect(() => {
    if (!user) return;
    if (unreadCount > prevUnread) {
      setPulse(true);
      setTimeout(() => setPulse(false), 1200);
    }
    setPrevUnread(unreadCount);
  }, [unreadCount, prevUnread, user]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render anything if not logged in
  if (!user) return null;

  const handleClick = async (id: string, isRead: boolean, link: string | null) => {
    if (!isRead) await markAsRead(id);
    if (link) navigate(link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((p) => !p)}
        aria-label="Notificações"
        className={`relative w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-300 cursor-pointer ${
          open
            ? 'text-[#D4AF37] bg-[#D4AF37]/10'
            : 'text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.07]'
        }`}
        style={{ border: open ? '1px solid rgba(212,175,55,0.3)' : '1px solid transparent' }}
      >
        <i className={`ri-notification-3-line text-base ${pulse ? 'animate-bounce' : ''}`}></i>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[17px] h-[17px] flex items-center justify-center rounded-full text-[9px] font-black text-white px-1 leading-none"
            style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: '0 2px 6px rgba(239,68,68,0.5)' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}

        {/* Live pulse ring when new notifs arrive */}
        {pulse && (
          <span
            className="absolute inset-0 rounded-xl animate-ping opacity-40"
            style={{ background: 'rgba(212,175,55,0.3)' }}
          />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-12 z-[200] overflow-hidden"
          style={{
            width: '340px',
            borderRadius: '18px',
            background: 'linear-gradient(160deg, rgba(14,14,26,0.98) 0%, rgba(10,10,18,0.97) 100%)',
            border: '1px solid rgba(212,175,55,0.18)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset',
            backdropFilter: 'blur(32px)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-3.5"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-notification-3-fill text-[#D4AF37] text-sm"></i>
              </span>
              <span className="text-white text-sm font-bold">Notificações</span>
              {unreadCount > 0 && (
                <span
                  className="text-[10px] font-black text-[#D4AF37] px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.25)' }}
                >
                  {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="text-[10px] text-white/30 hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap"
              >
                Marcar todas lidas
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-[360px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-12 px-5">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-2xl"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <i className="ri-notification-off-line text-2xl text-white/20"></i>
                </div>
                <div className="text-center">
                  <p className="text-white/50 text-sm font-medium mb-1">Tudo em dia!</p>
                  <p className="text-white/25 text-xs">Você não tem notificações no momento.</p>
                </div>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                {notifications.map((n) => {
                  const icon = ICON_MAP[n.type] || ICON_MAP.default;
                  const bg = BG_MAP[n.type] || BG_MAP.default;
                  const color = TEXT_MAP[n.type] || TEXT_MAP.default;
                  return (
                    <div
                      key={n.id}
                      onClick={() => handleClick(n.id, n.is_read, n.link)}
                      className="flex items-start gap-3.5 px-5 py-3.5 hover:bg-white/[0.03] cursor-pointer transition-colors duration-200 group"
                      style={!n.is_read ? { background: 'rgba(212,175,55,0.03)' } : {}}
                    >
                      {/* Icon */}
                      <div
                        className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 mt-0.5"
                        style={{ background: bg, border: `1px solid ${color}30` }}
                      >
                        <i className={`${icon} text-sm`} style={{ color }}></i>
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs leading-relaxed line-clamp-2 ${
                            !n.is_read ? 'text-white font-semibold' : 'text-white/65'
                          }`}
                        >
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-[11px] text-white/35 mt-0.5 line-clamp-1">{n.body}</p>
                        )}
                        <p className="text-[10px] text-white/20 mt-1.5">{timeAgo(n.created_at)}</p>
                      </div>

                      {/* Unread indicator */}
                      {!n.is_read && (
                        <div
                          className="w-2 h-2 rounded-full shrink-0 mt-1.5"
                          style={{ background: '#D4AF37', boxShadow: '0 0 6px rgba(212,175,55,0.6)' }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className="px-5 py-3"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            >
              <button
                onClick={() => setOpen(false)}
                className="w-full text-center text-xs text-white/25 hover:text-[#D4AF37] transition-colors cursor-pointer py-0.5"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
